import { gql, useMutation } from '@apollo/client';
import { Button, Container, Divider, FlexGrid, Header, Image, Loader, Picture, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Markdown from 'react-markdown';
import { useLocation } from 'react-router';
import useImageSrcBuilder from '../../../lib/hooks/useImageSrcBuilder';
import { interpolate } from '../../../lib/utils';
import formatPrice from '../../../lib/utils/formatPrice';
import generateCMSMetaTags from '../../../lib/utils/generateCMSMetaTags';
import Localytics from '../../../lib/utils/localytics';
import { LandingPagePropType } from '../../prop-types/cms-page';
import Markup from '../Markup';
import Page from '../Page';
import { NO_TOP_AD } from '../Page/Templates';
import styles from './LandingPageTemplate1.module.css';

const cx = classnames.bind(styles);

const PAGE_NAME = 'Pay_Landing';
const PAGE_DESCRIPTION = 'When you Pay with GasBuddy you can save up to 25¢/gallon on every fill-up at the pump. It\'s free to join and accepted at popular gas stations nationwide.';

function formatAlt(alt) {
  return `Pay with GasBuddy - Apply for Gas Card - ${alt}`;
}

function QuestionListItem({ children, question }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleClick = useCallback(() => {
    setIsOpen(isCurrentlyOpen => !isCurrentlyOpen);
  }, []);

  // See below link for reasoning behind button element
  // https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-noninteractive-element-interactions.md#case-this-is-a-heading-that-expandscollapses-content-on-the-package
  return (
    <div className={cx('item', { active: isOpen })} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
      <button className={cx('reset')} onClick={handleToggleClick} type="button">
        <span className={cx('chevron', isOpen ? 'up' : 'down')} />
        <strong className={cx('question')} itemProp="name">{question}</strong>
      </button>
      <div className={cx('answer')} itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
        <div itemProp="text">{children}</div>
      </div>
    </div>
  );
}

QuestionListItem.propTypes = {
  children: PropTypes.node,
  question: PropTypes.string,
};

QuestionListItem.defaultProps = {
  children: undefined,
  question: undefined,
};

function Quote({ children, image, imageAlt, name }) {
  return (
    <FlexGrid className={cx('quoteRow')} container>
      <FlexGrid.Column tablet={4}>
        <Image
          src={image}
          alt={imageAlt || formatAlt(name)}
        />
      </FlexGrid.Column>
      <FlexGrid.Column className={cx('quoteCol')} tablet={8}>
        <div className={cx('quote')}>
          <Image
            src="https://static.gasbuddy.com/web/pay/landing/Quote.svg"
            alt={formatAlt('Quote Mark')}
          />
          <blockquote>
            {children}
          </blockquote>
          <strong>{name}</strong>
        </div>
      </FlexGrid.Column>
    </FlexGrid>
  );
}

Quote.propTypes = {
  children: PropTypes.node.isRequired,
  image: PropTypes.string.isRequired,
  imageAlt: PropTypes.string,
  name: PropTypes.string.isRequired,
};

Quote.defaultProps = {
  imageAlt: undefined,
};

const TAG_ACCOUNT = gql`
  mutation($tagType: String!, $value: String!) {
    tagAccount(tagType: $tagType, value: $value) {
      success
    }
  }
`;


function HowToSave({ title, description, href, onClick, ctaLabel, imgSrc, imageAlt }) {
  return (
    <section className={cx('howToPay')}>
      <Container>
        <FlexGrid className={cx('textAndImageWrapper')} container>
          <FlexGrid.Column className={cx('textWrapperCol')} tablet={7}>
            <div className={cx('textWrapper')}>
              <Header as="h2">{title}</Header>
              <p>
                <Markup source={interpolate(description)} />
              </p>
              <Button
                as="a"
                href={href}
                className={cx('cta')}
                onClick={onClick}
                primary
                size="lg"
              >
                {ctaLabel}
              </Button>
            </div>
          </FlexGrid.Column>
          <FlexGrid.Column className={cx('imgWrapper')} tablet={5}>
            <Image
              src={imgSrc}
              alt={imageAlt}
            />
          </FlexGrid.Column>
        </FlexGrid>
        <hr />
      </Container>
    </section>
  );
}

HowToSave.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  ctaLabel: PropTypes.string,
  imgSrc: PropTypes.string,
  imageAlt: PropTypes.string,
};

HowToSave.defaultProps = {
  title: undefined,
  description: undefined,
  href: undefined,
  onClick: () => { },
  ctaLabel: undefined,
  imgSrc: undefined,
  imageAlt: undefined,
};

export function Hero({ buttonHref, heroData, imagesHost, offerPrice, onButtonClick }) {
  const imageToSrc = useImageSrcBuilder(imagesHost);
  return (
    <React.Fragment>
      {!!heroData && (
        <Helmet>
          <style>
            {`
              .${cx('banner')} {
                background-image: url(${imageToSrc(heroData.mobile_image)});
              }
              @media (min-width: 992px) {
                .${cx('banner')} {
                  background-image: url(${imageToSrc(heroData.image)});
                }
              }
            `}
          </style>
        </Helmet>
      )}
      <section className={cx('banner')}>
        <div className={cx('bannerText')}>
          <Header>
            <Markup source={interpolate(heroData?.title)} />
          </Header>
          <Markup source={interpolate(heroData?.description)} />
          <div className={cx('cta', 'hero-cta-wrapper')}>
            {!!offerPrice && (
              <Text bold centered className={cx('price')} fluid size="lg">Only {formatPrice(offerPrice)}/month*</Text>
            )}
            <Button
              as="a"
              fluid
              href={buttonHref}
              onClick={onButtonClick}
              primary
              size="lg"
            >
              {heroData?.cta_label}
            </Button>
          </div>
        </div>
      </section>
    </React.Fragment>
  );
}

Hero.propTypes = {
  buttonHref: PropTypes.string.isRequired,
  heroData: PropTypes.shape({
    cta_label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
  imagesHost: PropTypes.string,
  offerPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onButtonClick: PropTypes.func.isRequired,
};

Hero.defaultProps = {
  heroData: {},
  imagesHost: undefined,
  offerPrice: undefined,
};

export default function LandingPageTemplate1({ imagesHost, page }) {
  const faqs = page?.faqs?.questions || page?.faqs || [];
  const faqSet1 = faqs?.slice(0, Math.ceil(faqs.length / 2));
  const faqSet2 = faqs?.slice(faqSet1.length);
  const meta = page?.meta?.find(el => el.type === 'meta');
  const howItWorksSection = page?.section_list?.find(section => section.name === 'howItWorks');
  const howToSaveTopSection = page?.section_list?.find(section => section.name === 'howToSaveTop');
  const heroSection = page?.sections?.find(section => section.name === 'hero');
  const bottomHeroSection = page?.sections?.find(section => section.name === 'bottomHero');
  const howToSaveBottom = page?.sections?.find(section => section.name === 'howToSave');
  const phoneSection = page?.sections?.find(section => section.name === 'phone');
  const prImages = page?.image_sets?.find(imageSet => imageSet.name === 'PR Images')?.images;
  const metricImages = page?.image_sets?.find(imageSet => imageSet.name === 'Value Prop Images')?.images;
  const phoneImages = page?.image_sets?.find(imageSet => imageSet.name === 'Phone Value Prop Images')?.images;
  const markupImages = page?.image_sets?.find(imageSet => imageSet.name === 'Structured Markup Image Objects')?.images;
  const phoneImageSet1 = phoneImages?.slice(0, Math.ceil(phoneImages.length / 2));
  const phoneImageSet2 = phoneImages?.slice(phoneImageSet1.length);

  const location = useLocation();
  const imageToSrc = useImageSrcBuilder(imagesHost);
  const attachQueryString = baseUrl => `${baseUrl}${location.search}`;

  const primaryImageObjDetails = markupImages && {
    contentUrl: imageToSrc(markupImages[0].image),
    description: markupImages[0].alt,
    name: imageToSrc(markupImages[0].image).slice(0, -4),
  };

  const accountTagEvents = page?.actions?.filter(action => action.type === 'tag_account') || [];
  const PageViewTag = accountTagEvents?.find(event => event.trigger === 'view');
  const localyticsEvents = page?.actions?.filter(action => action.type === 'send_localytics') || [];
  const PageViewEvent = localyticsEvents?.find(event => event.trigger === 'view');
  const CTAClickEvent = localyticsEvents?.find(event => event.trigger === 'click');

  const [tagAccount] = useMutation(TAG_ACCOUNT);

  const createCTAClickHandler = useCallback(section => () => {
    if (CTAClickEvent) {
      const attributes = section ? { section_name: section } : {};

      if (CTAClickEvent.event_attributes) {
        CTAClickEvent.event_attributes.forEach((attr) => {
          attributes[attr.name] = attr.value;
        });
      }

      Localytics.event(CTAClickEvent.name, attributes);
    }
  }, [CTAClickEvent]);

  useEffect(() => {
    if (PageViewTag) {
      tagAccount({
        variables: {
          tagType: PageViewTag.tag_type,
          value: PageViewTag.value,
        },
      });
    }
  }, [PageViewTag, tagAccount]);

  useEffect(() => {
    if (PageViewEvent) {
      Localytics.pageview(PageViewEvent.name, undefined, ''); // Reset postfix to '' since _Opened is part of event already
    }
  }, [PageViewEvent]);

  const productSchema = page?.product && { product: page.product, helpers: { imageToSrc } };
  const price = page?.product?.offers?.[0]?.price?.amount;
  const partnerLogo = page?.partner_logo;

  return (
    <Page
      className={cx('payLanding')}
      name={PAGE_NAME}
      {...NO_TOP_AD}
      compact
      primaryImageObjDetails={primaryImageObjDetails}
      title={page?.title}
      productSchema={productSchema}
      description={meta?.content}
      withFooter={!partnerLogo}
      withNav={!partnerLogo}
    >
      <Helmet>
        {page?.title && (
          <title>{page.title}</title>
        )}
        {page?.canonical_url && (
          <link rel="canonical" href={attachQueryString(page.canonical_url)} />
        )}
        <meta name="description" content={PAGE_DESCRIPTION} />

        {!!page?.meta?.length && generateCMSMetaTags(page.meta, imageToSrc)}
      </Helmet>

      {!page
        ? (<Loader />)
        : (
          <React.Fragment>
            {partnerLogo && (
              <Container className={cx('header')}>
                <Image
                  alt="GasBuddy"
                  className={cx('brand')}
                  src="https://static.gasbuddy.com/web/gas-buddy-logo-midnight-txt.svg"
                />
                <Divider flex thick vertical />
                <Image
                  alt={partnerLogo.alt}
                  className={cx('brand', 'partner')}
                  src={imageToSrc(partnerLogo.image)}
                />
              </Container>
            )}

            <Hero
              buttonHref={attachQueryString(heroSection?.cta_url)}
              heroData={heroSection}
              imagesHost={imagesHost}
              offerPrice={price}
              onButtonClick={createCTAClickHandler(heroSection?.name)}
            />

            {(page?.save_headline || !!prImages?.length) && (
              <section className={cx('featured')}>
                <Container>
                  {page?.save_headline && (
                    <Header as="h2">
                      <Markup source={interpolate(page.save_headline)} />
                    </Header>
                  )}
                  {!!prImages?.length && (
                    <div className={cx('logoRow')}>
                      {!!prImages?.length && prImages.map(({ alt, image }) => (
                        <div className={cx('logoCol')} key={image.url}>
                          <Image
                            src={imageToSrc(image)}
                            alt={alt}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <hr />
                </Container>
              </section>
            )}

            {howItWorksSection && (
              <section className={cx('howItWorks')}>
                <Container>
                  <Header as="h2">{howItWorksSection.title}</Header>
                  <div className={cx('steps')}>
                    {howItWorksSection.blocks.map(({ alt, description, image, title }) => (
                      <div className={cx('step')} key={alt}>
                        <div> {/** Need this to use flex layout with ::before elment */}
                          <Image
                            src={imageToSrc(image)}
                            alt={alt}
                          />
                          <Text bold color="red" fluid size="xl" uppercase>{title}</Text>
                          <Text as="p">{description}</Text>
                        </div>
                      </div>
                    ))}
                  </div>
                  <hr />
                </Container>
              </section>
            )}

            {howToSaveTopSection && (
              <HowToSave
                title={howToSaveTopSection.title}
                description={howToSaveTopSection.blocks[0].description}
                href={attachQueryString(howToSaveTopSection.blocks[0].cta_url)}
                onClick={createCTAClickHandler(howToSaveTopSection.name)}
                ctaLabel={howToSaveTopSection.blocks[0].cta_label}
                imgSrc={imageToSrc(howToSaveTopSection.blocks[0].image)}
                imageAlt={howToSaveTopSection.blocks[0].alt}
              />
            )}

            {page?.quotes?.[0] && (
              <section className={cx('quoteSection')}>
                <Container>
                  <Quote
                    image={imageToSrc(page.quotes[0].quote.image)}
                    imageAlt={page.quotes[0].quote.alt}
                    name={page.quotes[0].quote.name}
                  >
                    <Markup source={interpolate(page.quotes[0].quote.quote)} />
                  </Quote>
                </Container>
              </section>
            )}

            <section className={cx('blueBg')}>
              <Container>
                <Header as="h2">{phoneSection?.title}</Header>
                <FlexGrid container>
                  <FlexGrid.Column mobile={6} tablet={4}>
                    {!!phoneImageSet1?.length && (
                      <FlexGrid container>
                        {phoneImageSet1.map(({ alt, content, image }) => (
                          <FlexGrid.Column key={image.url}>
                            <Image
                              className={cx('icon')}
                              src={imageToSrc(image)}
                              alt={alt}
                            />
                            <p className={cx('description')}>
                              <Markup source={interpolate(content)} />
                            </p>
                          </FlexGrid.Column>
                        ))}
                      </FlexGrid>
                    )}
                  </FlexGrid.Column>
                  <FlexGrid.Column className={cx('phoneImgCol')} tablet={4}>
                    <Image
                      className={cx('phoneImg')}
                      src={imageToSrc(phoneSection?.image)}
                      alt={phoneSection?.image_alt}
                    />
                  </FlexGrid.Column>
                  <FlexGrid.Column mobile={6} tablet={4}>
                    {!!phoneImageSet2?.length && (
                      <FlexGrid container>
                        {phoneImageSet2.map(({ alt, content, image }) => (
                          <FlexGrid.Column key={image.url}>
                            <Image
                              className={cx('icon')}
                              src={imageToSrc(image)}
                              alt={alt}
                            />
                            <p className={cx('description')}>
                              <Markup source={interpolate(content)} />
                            </p>
                          </FlexGrid.Column>
                        ))}
                      </FlexGrid>
                    )}
                  </FlexGrid.Column>
                  <FlexGrid.Column>
                    <Button
                      as="a"
                      href={attachQueryString(phoneSection?.cta_url)}
                      className={cx('cta')}
                      onClick={createCTAClickHandler(phoneSection?.name)}
                      primary
                      size="lg"
                    >
                      {phoneSection?.cta_label}
                    </Button>
                  </FlexGrid.Column>
                </FlexGrid>
              </Container>
            </section>

            {page?.quotes?.[1] && (
              <section className={cx('quoteSection')}>
                <Container>
                  <Quote
                    image={imageToSrc(page.quotes[1].quote.image)}
                    imageAlt={page.quotes[1].quote.alt}
                    name={page.quotes[1].quote.name}
                  >
                    <Markup source={interpolate(page.quotes[1].quote.quote)} />
                  </Quote>
                  <hr />
                </Container>
              </section>
            )}

            {howToSaveBottom && (
              <HowToSave
                title={howToSaveBottom.title}
                description={howToSaveBottom.description}
                href={attachQueryString(howToSaveBottom.cta_url)}
                onClick={createCTAClickHandler(howToSaveBottom.name)}
                ctaLabel={howToSaveBottom.cta_label}
                imgSrc={imageToSrc(howToSaveBottom.image)}
                imageAlt={howToSaveBottom.image_alt}
              />
            )}

            {page?.quotes?.[2] && (
              <section className={cx('quoteSection')}>
                <Container>
                  <Quote
                    image={imageToSrc(page.quotes[2].quote.image)}
                    imageAlt={page.quotes[2].quote.alt}
                    name={page.quotes[2].quote.name}
                  >
                    <Markup source={interpolate(page.quotes[2].quote.quote)} />
                  </Quote>
                  <hr />
                </Container>
              </section>
            )}

            {!!metricImages?.length && (
              <section className={cx('stats')}>
                <Container>
                  <div className={cx('statsRow')}>
                    <Header as="h2">
                      <Markup source={interpolate(page?.stats_headline)} />
                    </Header>

                    <FlexGrid container>
                      {metricImages.map(({ alt, image, content }) => (
                        <FlexGrid.Column className={cx('statWrapper')} key={image.url} mobile={6} tablet={3}>
                          <Image
                            src={imageToSrc(image)}
                            alt={alt}
                          />
                          <div className={cx('textWrapper')}>
                            <Markup source={interpolate(content)} />
                          </div>
                        </FlexGrid.Column>
                      ))}
                    </FlexGrid>
                  </div>
                  <hr />
                </Container>
              </section>
            )}

            <section className={cx('QAndA')}>
              <Container>
                <Header as="h2">{page?.faqs?.title || 'Pay with GasBuddy FAQ'}</Header>

                <FlexGrid className={cx('accordionRow')} container itemScope itemType="https://schema.org/FAQPage">
                  <FlexGrid.Column className={cx('accordionCol')} tablet={6}>
                    {faqSet1.map(({ faq }) => (
                      <QuestionListItem key={faq.title} question={faq.question}>
                        <Markdown source={interpolate(faq.answer)} escapeHtml={false} />
                      </QuestionListItem>
                    ))}
                  </FlexGrid.Column>

                  <FlexGrid.Column className={cx('accordionCol')} tablet={6}>
                    {faqSet2.map(({ faq }) => (
                      <QuestionListItem key={faq.title} question={faq.question}>
                        <Markdown source={interpolate(faq.answer)} escapeHtml={false} />
                      </QuestionListItem>
                    ))}
                  </FlexGrid.Column>
                </FlexGrid>

              </Container>
            </section>

            {bottomHeroSection && (
              <section className={cx('dontMissOut')}>
                <Container className={cx('container')}>
                  <div className={cx('textBoxWrapper')}>
                    <Header as="h2">{bottomHeroSection.title}</Header>
                    <p>
                      <Markup source={interpolate(bottomHeroSection.description)} />
                    </p>
                    <Button
                      as="a"
                      href={attachQueryString(bottomHeroSection.cta_url)}
                      className={cx('cta')}
                      onClick={createCTAClickHandler(bottomHeroSection.name)}
                      primary
                      size="lg"
                    >
                      {bottomHeroSection.cta_label}
                    </Button>
                  </div>
                </Container>
                <Picture
                  alt={bottomHeroSection.image_alt}
                  src={imageToSrc(bottomHeroSection.image)}
                >
                  <Picture.Source
                    media="(max-width: 991px)"
                    srcSet={imageToSrc(bottomHeroSection.mobile_image)}
                  />
                </Picture>
              </section>
            )}
          </React.Fragment>
        )
      }
    </Page>
  );
}

LandingPageTemplate1.propTypes = {
  imagesHost: PropTypes.string,
  page: LandingPagePropType,
};

LandingPageTemplate1.defaultProps = {
  imagesHost: undefined,
  page: undefined,
};
